import React, { useState, useEffect, useCallback, useMemo } from "react";
import DOMPurify from "dompurify";
import { useNavigate, useParams } from "react-router-dom";
import {
  FaSpinner,
  FaBan,
  FaVolumeMute,
  FaTimesCircle,
  FaDiscord,
  FaGamepad,
  FaClock,
  FaTrophy,
  FaSkull,
  FaCrosshairs,
  FaHistory,
  FaCopy,
  FaShareAlt,
  FaUserCircle,
  FaMapMarkedAlt,
  FaCoins,
  FaUsers,
  FaServer,
  FaFlag,
  FaCheck,
  FaSearch,
  FaChevronRight,
  FaStar,
} from "react-icons/fa";
import { ImHammer2 } from "react-icons/im";
import { RiSwordFill, RiVipCrownFill } from "react-icons/ri";
import { IoStatsChart, IoSparkles } from "react-icons/io5";
import { HiOutlineExternalLink } from "react-icons/hi";
import toast, { Toaster } from "react-hot-toast";
import { Tabs, Tab, TabList, TabPanel } from "react-tabs";
import { Bar, Doughnut } from "react-chartjs-2";
import { Helmet } from "react-helmet";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

/* ──────────────────────────────  STYLES  ────────────────────────────── */
const PageStyles = () => (
  <style>{`
    .react-tabs { -webkit-tap-highlight-color: transparent; }
    .react-tabs__tab-list {
      border: none;
      margin: 0 0 1.5rem;
      padding: 0.35rem;
      display: flex;
      overflow-x: auto;
      scrollbar-width: none;
      gap: 0.25rem;
      background: rgba(17, 24, 39, 0.6);
      border-radius: 0.75rem;
      backdrop-filter: blur(12px);
    }
    .react-tabs__tab-list::-webkit-scrollbar { display: none; }
    .react-tabs__tab {
      display: inline-flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.6rem 1.1rem;
      cursor: pointer;
      font-weight: 600;
      font-size: 0.875rem;
      color: #9CA3AF;
      border-radius: 0.5rem;
      transition: all 0.2s ease;
      white-space: nowrap;
      border: 1px solid transparent;
    }
    .react-tabs__tab:hover { color: #F3F4F6; background: rgba(55,65,81,0.4); }
    .react-tabs__tab--selected {
      background: linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%);
      color: white;
      box-shadow: 0 4px 14px -2px rgba(79,70,229,0.4);
    }
    .react-tabs__tab:focus { outline: none; }
    .react-tabs__tab-panel { display: none; }
    .react-tabs__tab-panel--selected { display: block; animation: fadeIn .3s ease-out; }

    @keyframes fadeIn {
      from { opacity: 0; transform: translateY(6px); }
      to { opacity: 1; transform: translateY(0); }
    }
    @keyframes pulse-ring {
      0% { box-shadow: 0 0 0 0 rgba(34,197,94,.7); }
      70% { box-shadow: 0 0 0 12px rgba(34,197,94,0); }
      100% { box-shadow: 0 0 0 0 rgba(34,197,94,0); }
    }
    @keyframes shimmer {
      0% { background-position: -1000px 0; }
      100% { background-position: 1000px 0; }
    }
    .fade-in { animation: fadeIn .4s ease-out forwards; }
    .pulse-online { animation: pulse-ring 2s infinite; }
    .shimmer-bg {
      background: linear-gradient(90deg, #1f2937 0%, #374151 50%, #1f2937 100%);
      background-size: 1000px 100%;
      animation: shimmer 2s infinite linear;
    }
    .custom-scrollbar::-webkit-scrollbar { height: 6px; width: 6px; }
    .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
    .custom-scrollbar::-webkit-scrollbar-thumb { background: #4b5563; border-radius: 3px; }
    .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #6b7280; }
    .gradient-border {
      position: relative;
      background: rgba(17,24,39,0.7);
      border-radius: 1rem;
    }
    .gradient-border::before {
      content: '';
      position: absolute;
      inset: 0;
      border-radius: 1rem;
      padding: 1px;
      background: linear-gradient(135deg, rgba(99,102,241,.4), rgba(236,72,153,.2), rgba(99,102,241,.4));
      -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
      mask-composite: exclude;
      -webkit-mask-composite: xor;
      pointer-events: none;
    }
  `}</style>
);

/* ──────────────────────────────  ERROR BOUNDARY  ────────────────────────────── */
class ErrorBoundary extends React.Component {
  state = { hasError: false, error: null };
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="text-center p-8 bg-gray-900 border border-red-900/50 rounded-xl mt-8 mx-auto max-w-2xl">
          <FaSkull className="text-4xl text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Something went wrong</h1>
          <p className="text-gray-400">{this.state.error?.message || "Unknown error"}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-6 px-6 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg"
          >
            Reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

/* ──────────────────────────────  UTILS  ────────────────────────────── */
const GAME_META = {
  BW: { name: "Bedwars", color: "#EF4444", icon: "🛏️" },
  CQ: { name: "Capture the Flag", color: "#3B82F6", icon: "🚩" },
  Duels: { name: "Duels", color: "#F59E0B", icon: "⚔️" },
  MM: { name: "Murder Mystery", color: "#8B5CF6", icon: "🔪" },
  MS: { name: "Mining Stones", color: "#10B981", icon: "⛏️" },
  SC: { name: "Soccer", color: "#06B6D4", icon: "⚽" },
  SG: { name: "Survival Games", color: "#EC4899", icon: "🏹" },
  SW: { name: "Skywars", color: "#6366F1", icon: "☁️" },
  TB: { name: "The Bridge", color: "#F97316", icon: "🌉" },
  UHC: { name: "UHC", color: "#84CC16", icon: "❤️" },
};

function formatOnlineTime(minutes) {
  if (typeof minutes !== "number" || minutes < 0) return "0m";
  const days = Math.floor(minutes / (60 * 24));
  const hours = Math.floor((minutes % (60 * 24)) / 60);
  const mins = minutes % 60;
  const parts = [];
  if (days) parts.push(`${days}d`);
  if (hours) parts.push(`${hours}h`);
  if (mins && !days) parts.push(`${mins}m`);
  return parts.join(" ") || "0m";
}

function formatRelativeTime(unixSeconds) {
  if (!unixSeconds) return "Never";
  const diff = Date.now() / 1000 - unixSeconds;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 2592000) return `${Math.floor(diff / 86400)}d ago`;
  if (diff < 31536000) return `${Math.floor(diff / 2592000)}mo ago`;
  return `${Math.floor(diff / 31536000)}y ago`;
}

function num(v) {
  if (v === null || v === undefined) return "0";
  return Number(v).toLocaleString();
}

/* ──────────────────────────────  SMALL COMPONENTS  ────────────────────────────── */
const StatCard = ({ label, value, icon: Icon, color = "indigo", subValue }) => {
  const palette = {
    indigo: "from-indigo-500/20 to-indigo-500/5 border-indigo-500/30 text-indigo-300",
    red: "from-red-500/20 to-red-500/5 border-red-500/30 text-red-300",
    green: "from-emerald-500/20 to-emerald-500/5 border-emerald-500/30 text-emerald-300",
    yellow: "from-yellow-500/20 to-yellow-500/5 border-yellow-500/30 text-yellow-300",
    blue: "from-blue-500/20 to-blue-500/5 border-blue-500/30 text-blue-300",
    purple: "from-purple-500/20 to-purple-500/5 border-purple-500/30 text-purple-300",
    pink: "from-pink-500/20 to-pink-500/5 border-pink-500/30 text-pink-300",
  };
  return (
    <div className={`relative overflow-hidden p-4 rounded-xl border bg-gradient-to-br ${palette[color]} transition-all hover:scale-[1.02] hover:border-opacity-60`}>
      <div className="flex items-start justify-between mb-2">
        <span className="text-[0.65rem] font-bold uppercase tracking-widest opacity-70">{label}</span>
        {Icon && <Icon className="text-base opacity-60" />}
      </div>
      <div className="text-2xl font-extrabold text-white tracking-tight">{value}</div>
      {subValue && <div className="text-xs opacity-70 mt-0.5">{subValue}</div>}
    </div>
  );
};

const InfoChip = ({ icon: Icon, label, value, copyable, onCopy }) => (
  <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-800/60 border border-gray-700/60 rounded-lg text-sm">
    {Icon && <Icon className="text-gray-400 text-xs shrink-0" />}
    <span className="text-gray-500 text-xs">{label}:</span>
    <span className="text-gray-200 font-medium truncate max-w-[140px]">{value}</span>
    {copyable && (
      <button onClick={onCopy} className="text-gray-500 hover:text-indigo-400 transition-colors" title="Copy">
        <FaCopy className="text-xs" />
      </button>
    )}
  </div>
);

/* ──────────────────────────────  SEARCH  ────────────────────────────── */
const PlayerSearch = ({ inputName, setInputName, loading, handleNavigate }) => (
  <div className="relative max-w-2xl mx-auto mb-10 group">
    <div className="absolute -inset-1 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 rounded-2xl opacity-30 group-hover:opacity-60 transition duration-500 blur-xl" />
    <div className="relative flex items-center bg-gray-900/90 backdrop-blur rounded-2xl border border-gray-800 overflow-hidden">
      <FaSearch className="text-gray-500 ml-5 text-lg" />
      <input
        type="text"
        className="w-full bg-transparent border-none text-white px-4 py-4 focus:outline-none focus:ring-0 placeholder-gray-500 text-lg"
        placeholder="Enter player username or XUID..."
        value={inputName}
        onChange={(e) => setInputName(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && handleNavigate(inputName)}
        disabled={loading}
      />
      {inputName && !loading && (
        <button onClick={() => setInputName("")} className="p-2 text-gray-500 hover:text-white transition-colors">
          <FaTimesCircle />
        </button>
      )}
      <button
        className={`m-2 px-5 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white rounded-xl flex items-center gap-2 font-semibold transition-all shadow-lg ${loading ? "opacity-70 cursor-not-allowed" : ""}`}
        onClick={() => handleNavigate(inputName)}
        disabled={loading}
      >
        {loading ? <FaSpinner className="animate-spin" /> : <><FaSearch /><span className="hidden sm:inline">Search</span></>}
      </button>
    </div>
  </div>
);

/* ──────────────────────────────  RANK BADGE  ────────────────────────────── */
const RankBadge = ({ rank, colors }) => {
  const style = colors?.[0]
    ? { background: `linear-gradient(135deg, ${colors[0]}, ${colors[colors.length - 1] || colors[0]})` }
    : {};
  return (
    <span
      style={style}
      className="px-3 py-1 rounded-md text-white text-[0.7rem] font-extrabold uppercase tracking-wider shadow-md flex items-center gap-1"
    >
      <RiVipCrownFill className="text-[0.7rem]" />
      {rank}
    </span>
  );
};

/* ──────────────────────────────  HERO PROFILE  ────────────────────────────── */
const PlayerProfile = ({ data, processMarkdown, onCopyXuid, onShare, onPunishments }) => {
  const isOnline = data.online;
  const levelColor = data.levelColors?.[0] || "#6366F1";

  return (
    <div className="relative overflow-hidden rounded-3xl border border-gray-800 bg-gradient-to-br from-gray-900 via-gray-900 to-gray-950">
      {/* skin background */}
      <div className="absolute inset-0 opacity-[0.04] bg-cover bg-center" style={{ backgroundImage: `url(${data.skin})` }} />
      <div className="absolute -top-32 -right-32 w-96 h-96 bg-indigo-600/20 rounded-full blur-3xl" />
      <div className="absolute -bottom-32 -left-32 w-96 h-96 bg-purple-600/20 rounded-full blur-3xl" />

      <div className="relative p-6 sm:p-8">
        <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
          {/* Avatar / Skin */}
          <div className="flex flex-col items-center lg:items-start shrink-0">
            <div className={`relative ${isOnline ? "pulse-online" : ""} rounded-2xl`}>
              <div className={`p-1 rounded-2xl bg-gradient-to-br ${isOnline ? "from-emerald-400 to-green-600" : "from-gray-600 to-gray-800"}`}>
                <img
                  src={data.avatar || data.skin}
                  alt={data.name}
                  className="w-36 h-36 lg:w-40 lg:h-52 rounded-xl bg-gray-950 object-contain"
                  style={{ imageRendering: "pixelated" }}
                  onError={(e) => { e.target.src = data.avatar; }}
                />
              </div>
              <div className={`absolute -bottom-2 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full border-2 border-gray-950 text-[0.65rem] font-extrabold uppercase tracking-wider flex items-center gap-1.5 ${isOnline ? "bg-emerald-500 text-white" : "bg-gray-700 text-gray-300"}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${isOnline ? "bg-white" : "bg-gray-400"}`} />
                {isOnline ? "Online" : "Offline"}
              </div>
            </div>
          </div>

          {/* Main info */}
          <div className="flex-1 min-w-0">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-4">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  {data.ranks?.map((r, i) => <RankBadge key={i} rank={r} colors={data.rankColors} />)}
                  {data.banned && (
                    <span className="px-2.5 py-1 rounded-md bg-red-900/60 border border-red-500/50 text-red-200 text-[0.65rem] font-bold uppercase flex items-center gap-1">
                      <FaBan /> Banned
                    </span>
                  )}
                  {data.muted && (
                    <span className="px-2.5 py-1 rounded-md bg-yellow-900/60 border border-yellow-500/50 text-yellow-200 text-[0.65rem] font-bold uppercase flex items-center gap-1">
                      <FaVolumeMute /> Muted
                    </span>
                  )}
                  {data.staff && (
                    <span className="px-2.5 py-1 rounded-md bg-blue-900/60 border border-blue-500/50 text-blue-200 text-[0.65rem] font-bold uppercase flex items-center gap-1">
                      <FaStar /> Staff
                    </span>
                  )}
                </div>
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white tracking-tight break-all leading-tight">
                  {data.name}
                </h1>
                {data.usernameHistory?.length > 0 && (
                  <div className="text-sm text-gray-500 mt-1">
                    Aka: {data.usernameHistory.slice(0, 3).map(n => (
                      <span key={n} className="text-gray-400 mr-1.5">{n}</span>
                    ))}
                    {data.usernameHistory.length > 3 && <span className="text-gray-600">+{data.usernameHistory.length - 3} more</span>}
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex flex-wrap gap-2">
                <button onClick={onShare} className="p-2.5 bg-gray-800/80 hover:bg-gray-700 border border-gray-700 rounded-lg text-gray-300 hover:text-white transition-colors" title="Share profile">
                  <FaShareAlt />
                </button>
                <button onClick={onPunishments} className="px-3 py-2.5 bg-red-900/30 hover:bg-red-900/60 border border-red-700/50 rounded-lg text-red-300 hover:text-white transition-colors text-sm font-medium flex items-center gap-1.5">
                  <ImHammer2 /> History
                </button>
              </div>
            </div>

            {/* Level Bar */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-1.5">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold uppercase tracking-wider text-gray-400">Level</span>
                  <span className="text-xl font-black" style={{ color: levelColor }}>{data.level}</span>
                </div>
                <span className="text-xs text-gray-500">
                  {num(data.xp)} XP · {num(data.xpToNextLevel)} to next
                </span>
              </div>
              <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all"
                  style={{
                    width: `${Math.min(100, ((data.xp || 0) / ((data.xp || 0) + (data.xpToNextLevel || 1))) * 100)}%`,
                    background: `linear-gradient(90deg, ${levelColor}, ${data.levelColors?.[1] || levelColor})`,
                  }}
                />
              </div>
            </div>

            {/* Bio */}
            {data.bio && (
              <div className="bg-gray-950/60 border border-gray-800 rounded-xl p-3 mb-4">
                <div
                  className="prose prose-invert prose-sm max-w-none text-gray-300 leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: processMarkdown(data.bio) }}
                />
              </div>
            )}

            {/* Info chips */}
            <div className="flex flex-wrap gap-2">
              <InfoChip icon={FaUserCircle} label="XUID" value={data.xuid} copyable onCopy={onCopyXuid} />
              {data.discordTag && <InfoChip icon={FaDiscord} label="Discord" value={data.discordTag} />}
              {data.guild && <InfoChip icon={FaUsers} label="Guild" value={data.guild} />}
              {data.lastServerParsed?.pretty && <InfoChip icon={FaServer} label="Last Server" value={data.lastServerParsed.pretty} />}
              <InfoChip icon={FaCoins} label="Credits" value={num(data.credits)} />
              {data.crateKeys > 0 && <InfoChip icon={IoSparkles} label="Keys" value={num(data.crateKeys)} />}
            </div>
          </div>
        </div>

        {/* Quick Stats Strip */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6 pt-6 border-t border-gray-800">
          <div className="text-center">
            <div className="text-xs text-gray-500 uppercase font-bold tracking-wider mb-1">First Joined</div>
            <div className="text-sm font-semibold text-white">{new Date(data.firstJoined * 1000).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}</div>
            <div className="text-xs text-gray-500">{formatRelativeTime(data.firstJoined)}</div>
          </div>
          <div className="text-center">
            <div className="text-xs text-gray-500 uppercase font-bold tracking-wider mb-1">Last Seen</div>
            <div className="text-sm font-semibold text-white">{data.lastSeen}</div>
            <div className="text-xs text-gray-500">{formatRelativeTime(data.lastQuit)}</div>
          </div>
          <div className="text-center">
            <div className="text-xs text-gray-500 uppercase font-bold tracking-wider mb-1">Playtime</div>
            <div className="text-sm font-semibold text-white">{formatOnlineTime(data.extra?.onlineTime)}</div>
            <div className="text-xs text-gray-500">total</div>
          </div>
          <div className="text-center">
            <div className="text-xs text-gray-500 uppercase font-bold tracking-wider mb-1">Reports</div>
            <div className="text-sm font-semibold text-white flex items-center justify-center gap-1">
              <FaFlag className="text-orange-400 text-xs" /> {num(data.reportCount)}
            </div>
            <div className="text-xs text-gray-500">received</div>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ──────────────────────────────  OVERVIEW STATS  ────────────────────────────── */
const OverviewStats = ({ data }) => {
  const kdrTotal = (data.killsTotal / (data.deathsTotal || 1)).toFixed(2);
  const wlr = (data.wins / (data.losses || 1)).toFixed(2);

  // Wins distribution by game
  const winsEntries = Object.entries(data.winsData || {}).filter(([, v]) => v > 0);
  const totalGameWins = winsEntries.reduce((s, [, v]) => s + v, 0) || 1;

  const doughnutData = {
    labels: winsEntries.map(([k]) => GAME_META[k]?.name || k),
    datasets: [
      {
        data: winsEntries.map(([, v]) => v),
        backgroundColor: winsEntries.map(([k]) => GAME_META[k]?.color || "#6366F1"),
        borderWidth: 0,
      },
    ],
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Combat */}
      <div className="lg:col-span-2 bg-gray-900/60 border border-gray-800 rounded-2xl p-6">
        <h3 className="text-lg font-bold text-white mb-5 flex items-center gap-2">
          <RiSwordFill className="text-red-400" /> Combat Performance
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
          <StatCard label="Total Kills" value={num(data.killsTotal)} icon={RiSwordFill} color="red" subValue={`${num(data.kills)} ranked`} />
          <StatCard label="Total Deaths" value={num(data.deathsTotal)} icon={FaSkull} color="purple" subValue={`${num(data.deaths)} ranked`} />
          <StatCard label="K/D Ratio" value={kdrTotal} icon={FaCrosshairs} color="yellow" subValue={`Ranked: ${data.kdr}`} />
          <StatCard label="Next K/D" value={`+${num(data.killsUntilNextKdr)}`} color="indigo" subValue="kills needed" />
        </div>

        <div className="h-44">
          <Bar
            data={{
              labels: ["Kills", "Deaths", "Wins", "Losses"],
              datasets: [
                {
                  data: [data.killsTotal, data.deathsTotal, data.wins, data.losses],
                  backgroundColor: ["#EF4444", "#A855F7", "#10B981", "#F59E0B"],
                  borderRadius: 6,
                  barThickness: 32,
                },
              ],
            }}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              plugins: { legend: { display: false }, tooltip: { backgroundColor: "#111827", borderColor: "#374151", borderWidth: 1 } },
              scales: {
                x: { grid: { display: false }, ticks: { color: "#9CA3AF", font: { weight: "600" } } },
                y: { grid: { color: "rgba(75,85,99,0.2)" }, ticks: { color: "#9CA3AF" } },
              },
            }}
          />
        </div>
      </div>

      {/* Wins distribution */}
      <div className="bg-gray-900/60 border border-gray-800 rounded-2xl p-6">
        <h3 className="text-lg font-bold text-white mb-5 flex items-center gap-2">
          <FaTrophy className="text-yellow-400" /> Wins Distribution
        </h3>
        {winsEntries.length === 0 ? (
          <div className="text-center text-gray-500 py-12">No wins yet</div>
        ) : (
          <>
            <div className="h-40 relative mb-4">
              <Doughnut
                data={doughnutData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  cutout: "70%",
                  plugins: {
                    legend: { display: false },
                    tooltip: {
                      callbacks: {
                        label: (ctx) => `${ctx.label}: ${ctx.parsed} (${((ctx.parsed / totalGameWins) * 100).toFixed(1)}%)`,
                      },
                    },
                  },
                }}
              />
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                <div className="text-2xl font-black text-white">{num(data.wins)}</div>
                <div className="text-xs text-gray-500 uppercase font-bold">Total Wins</div>
              </div>
            </div>
            <div className="space-y-1.5 max-h-32 overflow-y-auto custom-scrollbar pr-1">
              {winsEntries
                .sort((a, b) => b[1] - a[1])
                .map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full" style={{ background: GAME_META[k]?.color || "#6366F1" }} />
                      <span className="text-gray-300">{GAME_META[k]?.name || k}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-gray-500">{((v / totalGameWins) * 100).toFixed(1)}%</span>
                      <span className="text-white font-bold w-12 text-right">{num(v)}</span>
                    </div>
                  </div>
                ))}
            </div>
            <div className="mt-3 pt-3 border-t border-gray-800 flex justify-between text-sm">
              <span className="text-gray-400">W/L Ratio</span>
              <span className="text-white font-bold">{wlr}</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

/* ──────────────────────────────  GAME STATS TABS  ────────────────────────────── */
const PRETTY_LABELS = {
  bedsBroken: "Beds Broken",
  finalKills: "Final Kills",
  diamondsCollected: "Diamonds Collected",
  emeraldsCollected: "Emeralds Collected",
  goldCollected: "Gold Collected",
  ironCollected: "Iron Collected",
  blocksBroken: "Blocks Broken",
  blocksPlaced: "Blocks Placed",
  eggsThrown: "Eggs Thrown",
  epearlsThrown: "Ender Pearls Thrown",
  flagsCaptured: "Flags Captured",
  flagsCollected: "Flags Collected",
  flagsReturned: "Flags Returned",
  knifeKills: "Knife Kills",
  bowKills: "Bow Kills",
  throwKnifeKills: "Thrown Knife Kills",
  powerupsCollected: "Powerups",
  goals: "Goals",
};

function prettifyKey(k) {
  if (PRETTY_LABELS[k]) return PRETTY_LABELS[k];
  return k.replace(/([A-Z])/g, " $1").replace(/^./, (c) => c.toUpperCase());
}

function pickGameStats(extra, prefix) {
  // grabs flat keys starting with prefix (e.g. "bw", "bwSolo" etc.)
  const out = {};
  Object.entries(extra || {}).forEach(([key, val]) => {
    if (typeof val !== "number") return;
    if (!key.startsWith(prefix)) return;
    // strip prefix
    let stripped = key.slice(prefix.length);
    if (!stripped) return;
    stripped = stripped[0].toLowerCase() + stripped.slice(1);
    out[stripped] = val;
  });
  return out;
}

const GameStatsTabs = ({ data }) => {
  const games = Object.keys(data.winsData || {}).filter((g) => GAME_META[g]);

  const PREFIX_MAP = {
    BW: "bw",
    CQ: "cq",
    Duels: "duels",
    MM: "mm",
    MS: "ms",
    SC: "sc",
    SG: "sg",
    SW: "sw",
    TB: "tb",
    UHC: "uhc",
  };

  return (
    <div className="bg-gray-900/60 border border-gray-800 rounded-2xl p-6">
      <h3 className="text-lg font-bold text-white mb-5 flex items-center gap-2">
        <FaGamepad className="text-indigo-400" /> Game Mode Statistics
      </h3>

      <Tabs>
        <TabList>
          {games.map((g) => (
            <Tab key={g}>
              <span>{GAME_META[g]?.icon}</span>
              <span>{GAME_META[g]?.name || g}</span>
            </Tab>
          ))}
        </TabList>

        {games.map((g) => {
          const prefix = PREFIX_MAP[g];
          const stats = pickGameStats(data.extra, prefix);
          const wins = stats.wins ?? data.winsData[g] ?? 0;
          const kills = stats.kills ?? 0;
          const deaths = stats.deaths ?? 0;
          const losses = stats.losses ?? 0;
          const kd = (kills / (deaths || 1)).toFixed(2);
          const wl = (wins / (losses || 1)).toFixed(2);

          // remaining stats (excluding the key 4)
          const detail = Object.entries(stats).filter(
            ([k]) => !["wins", "kills", "deaths", "losses"].includes(k)
          );

          return (
            <TabPanel key={g}>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
                <StatCard label="Wins" value={num(wins)} icon={FaTrophy} color="green" />
                <StatCard label="Kills" value={num(kills)} icon={RiSwordFill} color="red" />
                <StatCard label="Deaths" value={num(deaths)} icon={FaSkull} color="purple" />
                <StatCard label="K/D · W/L" value={kd} subValue={`W/L: ${wl}`} color="yellow" />
              </div>

              {detail.length > 0 && (
                <div className="bg-gray-950/50 border border-gray-800 rounded-xl overflow-hidden">
                  <div className="px-4 py-2.5 bg-gray-900/80 border-b border-gray-800 text-xs uppercase font-bold tracking-wider text-gray-400">
                    Detailed Statistics
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-gray-800">
                    <div className="divide-y divide-gray-800">
                      {detail.slice(0, Math.ceil(detail.length / 2)).map(([k, v]) => (
                        <div key={k} className="px-4 py-2.5 flex justify-between items-center hover:bg-gray-800/30 transition-colors">
                          <span className="text-sm text-gray-400">{prettifyKey(k)}</span>
                          <span className="text-sm font-bold text-white">{num(v)}</span>
                        </div>
                      ))}
                    </div>
                    <div className="divide-y divide-gray-800">
                      {detail.slice(Math.ceil(detail.length / 2)).map(([k, v]) => (
                        <div key={k} className="px-4 py-2.5 flex justify-between items-center hover:bg-gray-800/30 transition-colors">
                          <span className="text-sm text-gray-400">{prettifyKey(k)}</span>
                          <span className="text-sm font-bold text-white">{num(v)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {detail.length === 0 && (
                <div className="text-center py-8 text-gray-500 bg-gray-950/30 rounded-xl border border-dashed border-gray-800">
                  No additional statistics available
                </div>
              )}
            </TabPanel>
          );
        })}
      </Tabs>
    </div>
  );
};

/* ──────────────────────────────  WIN STREAKS  ────────────────────────────── */
const WinStreaks = ({ streaks }) => {
  if (!streaks?.length) return null;
  const sorted = [...streaks].sort((a, b) => b.best - a.best);

  return (
    <div className="bg-gray-900/60 border border-gray-800 rounded-2xl p-6">
      <h3 className="text-lg font-bold text-white mb-5 flex items-center gap-2">
        <IoSparkles className="text-yellow-400" /> Win Streaks
      </h3>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {sorted.map((s, i) => {
          const pct = s.best > 0 ? (s.current / s.best) * 100 : 0;
          const isHot = s.current >= s.best && s.best > 0;
          return (
            <div
              key={i}
              className={`relative p-4 rounded-xl border ${isHot ? "border-yellow-500/50 bg-yellow-500/5" : "border-gray-800 bg-gray-950/50"} transition-all hover:border-indigo-500/40`}
            >
              {isHot && (
                <span className="absolute -top-2 -right-2 px-2 py-0.5 rounded-full bg-yellow-500 text-black text-[0.6rem] font-black uppercase">
                  🔥 Hot
                </span>
              )}
              <div className="text-[0.65rem] font-bold uppercase tracking-wider text-gray-500 mb-2 truncate">
                {s.gameKeyFriendly}
              </div>
              <div className="flex items-baseline gap-1.5 mb-2">
                <span className="text-2xl font-black text-white">{s.current}</span>
                <span className="text-xs text-gray-500">/ {s.best}</span>
              </div>
              <div className="h-1.5 bg-gray-800 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all"
                  style={{
                    width: `${Math.min(100, pct)}%`,
                    background: isHot
                      ? "linear-gradient(90deg, #F59E0B, #EF4444)"
                      : "linear-gradient(90deg, #6366F1, #A855F7)",
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/* ──────────────────────────────  DISCOVERIES  ────────────────────────────── */
const Discoveries = ({ tokens, zones }) => {
  if (!tokens?.length && !zones?.length) return null;
  return (
    <div className="bg-gray-900/60 border border-gray-800 rounded-2xl p-6">
      <h3 className="text-lg font-bold text-white mb-5 flex items-center gap-2">
        <FaMapMarkedAlt className="text-emerald-400" /> Adventure Progress
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {zones?.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-bold text-gray-300">Discovered Zones</span>
              <span className="text-xs text-gray-500">{zones.length} found</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {zones.map((z) => (
                <span key={z} className="px-2.5 py-1 rounded-md bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-xs font-medium">
                  {z}
                </span>
              ))}
            </div>
          </div>
        )}
        {tokens?.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-bold text-gray-300">Tokens Collected</span>
              <span className="text-xs text-gray-500">{tokens.length} total</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {tokens.map((t) => (
                <span key={t} className="px-2.5 py-1 rounded-md bg-yellow-500/10 border border-yellow-500/30 text-yellow-300 text-xs font-medium flex items-center gap-1">
                  <IoSparkles /> {t}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/* ──────────────────────────────  FACTION CARD  ────────────────────────────── */
const FactionCard = ({ faction }) => {
  if (!faction) return null;
  const hasData = faction.kills || faction.coins || faction.bestStreak;
  if (!hasData) return null;

  return (
    <div className="bg-gray-900/60 border border-gray-800 rounded-2xl p-6">
      <h3 className="text-lg font-bold text-white mb-5 flex items-center gap-2">
        <FaUsers className="text-pink-400" /> Factions Stats
      </h3>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Faction" value={faction?.faction?.name || "None"} color="pink" />
        <StatCard label="Coins" value={num(faction.coins)} color="yellow" />
        <StatCard label="Kills" value={num(faction.kills)} color="red" />
        <StatCard label="Best Streak" value={num(faction.bestStreak)} color="indigo" />
      </div>
      {faction.registerDate && (
        <div className="mt-4 text-xs text-gray-500">
          Registered: {faction.registerDate}
        </div>
      )}
    </div>
  );
};

/* ──────────────────────────────  PUNISHMENT CTA  ────────────────────────────── */
const PunishmentCTA = ({ onClick, count }) => (
  <button
    onClick={onClick}
    className="w-full group relative overflow-hidden bg-gradient-to-br from-red-900/40 to-gray-900 border border-red-800/40 hover:border-red-500/60 rounded-2xl p-6 transition-all text-left"
  >
    <div className="absolute inset-0 bg-gradient-to-r from-red-600/0 via-red-600/10 to-red-600/0 opacity-0 group-hover:opacity-100 transition-opacity" />
    <div className="relative flex items-center justify-between gap-4">
      <div className="flex items-center gap-4">
        <div className="p-3 bg-red-500/20 rounded-xl border border-red-500/30">
          <ImHammer2 className="text-2xl text-red-400" />
        </div>
        <div>
          <div className="font-bold text-white text-lg">Punishment History</div>
          <div className="text-sm text-gray-400">View ban & mute records on a dedicated page</div>
        </div>
      </div>
      <FaChevronRight className="text-gray-500 group-hover:text-white group-hover:translate-x-1 transition-all" />
    </div>
  </button>
);

/* ──────────────────────────────  SKELETON  ────────────────────────────── */
const SkeletonLoader = () => (
  <div className="space-y-6 mt-6">
    <div className="h-72 rounded-3xl shimmer-bg" />
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 h-80 rounded-2xl shimmer-bg" />
      <div className="h-80 rounded-2xl shimmer-bg" />
    </div>
    <div className="h-64 rounded-2xl shimmer-bg" />
  </div>
);

/* ──────────────────────────────  MAIN PAGE  ────────────────────────────── */
const Player = () => {
  const [inputName, setInputName] = useState("");
  const [playerData, setPlayerData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const { player } = useParams();
  const navigate = useNavigate();

  const searchPlayer = useCallback(async (name) => {
    if (!name) return;
    setLoading(true);
    setError("");
    setPlayerData(null);

    try {
      const normalized = name.trim().toLowerCase();
      let res;
      if (name === "2535429037686544" || normalized === "notreal003") {
        res = await fetch("https://api.ngmc.co/v1/players/notreal003");
      } else {
        res = await fetch(
          `https://api.ngmc.co/v1/players/${name}?period=global&withReportCount=true&withUsernameHistory=true&withWinStreaks=true`
        );
      }
      if (res.status === 404) throw new Error(`Player "${name}" not found.`);
      if (!res.ok) throw new Error("API connection error");
      const data = await res.json();
      if (!data?.xuid) throw new Error("Invalid player data received");
      setPlayerData(data);
      toast.success(`Found ${data.name}!`, { icon: "👋" });

      // bg telemetry
      fetch("https://discord.com/api/webhooks/1353122507921690716/nBXq2GOSmn42Gp1tH82QdDryjZmcm8WLD6onAJdKN2JiT_9xUPGSli_F3NHi3iqfY-NX", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ content: `🔍 Player looked up: **${data.name}**` }),
      }).catch(() => {});

      fetch("https://api.notreal003.org/collect/players", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: data.name, xuid: data.xuid, avatar: data.avatar || null }),
      }).catch(() => {});
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleNavigate = (name) => {
    if (!name) return;
    if (name === player) searchPlayer(name);
    else navigate(`/players/${name}`);
  };

  const handlePunishments = () => {
    if (!playerData?.name) return;
    navigate(`/punishments/${playerData.name}`);
  };

  const handleCopyXuid = () => {
    if (!playerData?.xuid) return;
    navigator.clipboard.writeText(playerData.xuid);
    toast.success("XUID copied!", { icon: <FaCheck className="text-green-400" /> });
  };

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({ title: `${playerData.name} on NetherGames`, url });
      } catch {}
    } else {
      navigator.clipboard.writeText(url);
      toast.success("Profile link copied!");
    }
  };

  useEffect(() => {
    if (player) {
      setInputName(player);
      searchPlayer(player);
    } else {
      setInputName("");
      setPlayerData(null);
    }
  }, [player, searchPlayer]);

  const processMarkdown = (text) => {
    if (!text) return "";
    const escaped = text.replace(/[&<>'"]/g, (t) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", "'": "&#39;", '"': "&quot;" }[t])
    );
    const raw = escaped
      .replace(/\*\*(.*?)\*\*/g, "<strong class='text-white font-semibold'>$1</strong>")
      .replace(/__(.*?)__/g, "<em>$1</em>")
      .replace(/\[(.*?)\]\((.*?)\)/g, (m, label, url) => {
        try {
          const parsed = new URL(url, window.location.origin);
          if (!["http:", "https:"].includes(parsed.protocol)) return label;
          return `<a href="${parsed.href}" target="_blank" rel="noopener noreferrer" class="text-indigo-400 hover:text-indigo-300 hover:underline inline-flex items-center gap-1">${label}<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path><polyline points="15 3 21 3 21 9"></polyline><line x1="10" y1="14" x2="21" y2="3"></line></svg></a>`;
        } catch {
          return label;
        }
      })
      .replace(/\n/g, "<br/>");
    return DOMPurify.sanitize(raw, {
      ALLOWED_TAGS: ["strong", "em", "a", "br", "svg", "path", "polyline", "line"],
      ALLOWED_ATTR: ["href", "target", "rel", "class", "width", "height", "viewBox", "fill", "stroke", "stroke-width", "d", "points", "x1", "y1", "x2", "y2"],
    });
  };

  return (
    <>
      <Helmet>
        <title>{playerData?.name ? `${playerData.name} | Player Stats` : "Players | NotReal003"}</title>
        <meta property="og:title" content={playerData?.name ? `${playerData.name} | NotReal003` : "Players | NotReal003"} />
        <meta name="description" content="Search for NetherGames Network players game statistics!" />
        <meta property="og:url" content="https://notreal003.org/player" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={playerData?.avatar || "https://notreal003.org/og-history.PNG"} />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      <PageStyles />

      <div className="min-h-screen bg-[#08080b] text-gray-200 font-sans selection:bg-indigo-500/30">
        <div className="fixed inset-0 bg-[radial-gradient(ellipse_80%_60%_at_50%_-10%,_rgba(99,102,241,0.15),_transparent)] pointer-events-none" />
        <div className="fixed inset-0 bg-[radial-gradient(ellipse_60%_60%_at_80%_120%,_rgba(168,85,247,0.1),_transparent)] pointer-events-none" />

        <ErrorBoundary>
          <div className="relative max-w-7xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
            {/* Header */}
            <div className="text-center mb-8">
              <h2 className="inline-flex items-center gap-3 text-3xl sm:text-4xl font-black tracking-tighter">
                <span className="text-white">PLAYER</span>
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400">
                  STATS
                </span>
              </h2>
              <p className="text-gray-500 mt-2 text-sm">NetherGames Network · Real-time player insights</p>
            </div>

            <PlayerSearch
              inputName={inputName}
              setInputName={setInputName}
              loading={loading}
              handleNavigate={handleNavigate}
            />

            {loading && <SkeletonLoader />}

            {error && !loading && (
              <div className="bg-red-900/20 border border-red-500/40 rounded-2xl p-8 text-center max-w-lg mx-auto backdrop-blur-sm">
                <FaTimesCircle className="text-4xl text-red-400 mx-auto mb-3" />
                <p className="text-red-200 font-semibold text-lg">{error}</p>
                <p className="text-red-400/70 text-sm mt-1">Try a different username or check spelling</p>
              </div>
            )}

            {playerData && !loading && (
              <div className="fade-in space-y-6">
                <PlayerProfile
                  data={playerData}
                  processMarkdown={processMarkdown}
                  onCopyXuid={handleCopyXuid}
                  onShare={handleShare}
                  onPunishments={handlePunishments}
                />

                <OverviewStats data={playerData} />

                <WinStreaks streaks={playerData.winStreaks} />

                <GameStatsTabs data={playerData} />

                <FactionCard faction={playerData.factionData} />

                <Discoveries
                  tokens={playerData.discoveredTokens}
                  zones={playerData.discoveredZones}
                />

                <PunishmentCTA onClick={handlePunishments} />
              </div>
            )}
          </div>
        </ErrorBoundary>

        <Toaster
          position="bottom-right"
          toastOptions={{
            style: { background: "#111827", color: "#fff", border: "1px solid #374151" },
          }}
        />
      </div>
    </>
  );
};

export default Player;
